import { createContext, useState, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ClientDto } from '@ata-digital/client-tax-system-api-client';
import { useAvailableClients } from '@/hooks/api/useClients';
import { useAuthState } from '@/hooks/auth/useAuth';

/**
 * Client context for managing client switching functionality
 * Uses ClientDto from API client package for type safety
 */
export interface ClientContextType {
  selectedClient: ClientDto | null;
  availableClients: ClientDto[];
  setSelectedClient: (client: ClientDto | null) => void;
  isLoadingClients: boolean;
  clientsError: Error | null;
  refetchClients: () => void;
}

export const ClientContext = createContext<ClientContextType | undefined>(undefined);

export function ClientProvider({ children }: { children: ReactNode }) {
  // Initialize selectedClient synchronously from sessionStorage to avoid race condition
  // where MainLayout hides nav because async queries haven't loaded yet
  const [selectedClient, setSelectedClientState] = useState<ClientDto | null>(() => {
    const stored = sessionStorage.getItem('selectedClient');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  });
  const { data: authState } = useAuthState();
  const queryClient = useQueryClient();

  // Fetch available clients from API
  const {
    data: availableClients = [],
    isLoading: isLoadingClients,
    error: clientsError,
    refetch,
  } = useAvailableClients();

  // Load selected client from sessionStorage on mount and when available clients change
  useEffect(() => {
    if (!availableClients || availableClients.length === 0) {
      return;
    }

    const storedClientId = sessionStorage.getItem('selectedClientId');

    if (storedClientId) {
      // Update selected client with fresh data from the API if found
      const client = availableClients.find((c) => c.id === storedClientId);
      if (client) {
        setSelectedClientState(client);
        // Also refresh the cached copy in sessionStorage. The X-Active-Client-Id
        // interceptor reads `selectedClient` from sessionStorage (not React
        // state), so persisting here keeps the header accurate and prevents a
        // stale/missing object from silently dropping the header.
        sessionStorage.setItem('selectedClient', JSON.stringify(client));
      }
      // If not found, keep the current selection — the user explicitly chose this client.
      // If access was truly revoked, API calls will return auth errors handled by the error interceptor.
    } else if (availableClients.length === 1 && authState?.userType === 'client') {
      // Auto-select if only one client available for client users
      const singleClient = availableClients[0];
      setSelectedClientState(singleClient);
      sessionStorage.setItem('selectedClientId', singleClient.id || '');
      sessionStorage.setItem('selectedClient', JSON.stringify(singleClient));
    }
  }, [availableClients, authState?.userType]);

  const setSelectedClient = (client: ClientDto | null) => {
    const previousClientId = selectedClient?.id;
    const newClientId = client?.id;

    // Always keep sessionStorage in sync — it may have been cleared externally
    // (e.g., by the client auth error interceptor) while context state still holds the old value.
    if (client) {
      sessionStorage.setItem('selectedClientId', client.id || '');
      sessionStorage.setItem('selectedClient', JSON.stringify(client));
    } else {
      sessionStorage.removeItem('selectedClientId');
      sessionStorage.removeItem('selectedClient');
    }

    // Only update React state and invalidate queries if the client actually changed
    if (previousClientId !== newClientId) {
      setSelectedClientState(client);

      // Invalidate all queries except auth and client queries when client changes
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          // Keep auth and client queries, invalidate everything else
          return !['auth', 'clients'].includes(queryKey[0] as string);
        },
      });
    }
  };

  return (
    <ClientContext.Provider
      value={{
        selectedClient,
        availableClients,
        setSelectedClient,
        isLoadingClients,
        clientsError: clientsError as Error | null,
        refetchClients: refetch,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
}
